import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAtom } from 'jotai';
import { X } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';

import { materialSettingsAtom, settingsChangedAtom } from '../../atoms/settings';
import { MATERIAL_OPTIONS, TYPE_MATERIAL_OPTIONS } from '../../constants/style-settings';
import { getColorFromName } from '../../utils/getColorFromName';

const MaterialItem = ({
  active,
  material,
  label,
  onClickItem,
}: {
  active: boolean;
  material: TYPE_MATERIAL_OPTIONS;
  label: string;
  onClickItem: (option: TYPE_MATERIAL_OPTIONS) => void;
}) => {
  return (
    <button
      className={cn(
        'flex cursor-pointer items-center gap-1 rounded-sm px-2 py-1',
        active ? 'bg-[#07B1BE1F]' : 'bg-[#0000001F]'
      )}
      onClick={() => onClickItem(material)}
    >
      <div
        className="h-[13px] w-[13px] rounded-full"
        style={{ backgroundColor: getColorFromName(material) }}
      />
      <span className="text-[10px] font-medium leading-none">{label}</span>
      {active ? <X className="h-[10px] w-[10px]" /> : null}
    </button>
  );
};

export const MaterialSettings = () => {
  const [materials, setMaterials] = useAtom(materialSettingsAtom);
  const [, setSettingsChanged] = useAtom(settingsChangedAtom);
  const { t } = useTranslation();
  const [isOpened, setIsOpened] = useState(false);

  const materialOptions = Object.values(MATERIAL_OPTIONS);

  const handleSelect = (option: TYPE_MATERIAL_OPTIONS) => {
    setMaterials((prev) => {
      if (prev.includes(option)) {
        return prev.filter((material) => material !== option);
      }

      return [...prev, option];
    });
    setSettingsChanged(true);
  };

  return (
    <Accordion
      type="single"
      className="w-full overflow-hidden rounded-sm bg-[#EFF4F5]"
      onValueChange={(val: string) => setIsOpened(!!val)}
      collapsible
    >
      <AccordionItem value="material-settings">
        <AccordionTrigger className="flex min-h-[40px] w-full cursor-pointer items-center rounded-b-sm px-4 py-2 sm:min-h-[26px] sm:px-2 sm:py-1 lg:min-h-[40px] lg:px-4 lg:py-2">
          <span className="flex-1 text-left text-xs font-semibold lg:text-sm">
            {t('materials')}
          </span>
        </AccordionTrigger>
        {!isOpened && materials.length > 0 ? (
          <div className="flex flex-wrap gap-1 px-4 pb-3">
            {materials.map((material) => (
              <MaterialItem
                key={material}
                material={material}
                label={t(material)}
                onClickItem={handleSelect}
                active
              />
            ))}
          </div>
        ) : null}
        <AccordionContent className="px-4 pb-3">
          <div className="flex flex-wrap gap-1">
            {materialOptions.map((material) => (
              <MaterialItem
                key={material}
                active={materials.includes(material)}
                material={material}
                label={t(material)}
                onClickItem={handleSelect}
              />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
